import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Input,
  Label,
  UncontrolledTooltip,
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../../components/Common";
import { PaginateQuery, TeacherAdminDto } from "../../../interfaces";
import { EducationalStageDto } from "../../../interfaces/curriculum";
import { SelectListDto } from "../../../models";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
  TableSpinner,
  TextColumn,
  TeacherSubjectsModal,
} from "../../../components";
import { ActionTypeEnum, RoleEnum } from "../../../enums";
import DeleteModal from "../../../components/Common/DeleteModal";
import { AddEditAdminTeacherModal } from "./AddEditAdminTeacherModal";
import {
  useGetTeacherAdminUsersQuery,
  useDeleteTeacherAdminUserMutation,
  useUpdateTeacherAdminUserProfitMutation,
} from "../../../store/api/teacherAdminApi";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";
import { useGetCurriculumSelectListQuery } from "../../../store/api/curriculumApi";
import { useGetCourseSelectListQuery } from "../../../store/api/courseApi";
import { useGetEducationalStagesQuery } from "../../../store/api/educationalStageApi";
import { useGetSubjectsSelectListByGradeQuery } from "../../../store/api/subjectApi";
// @ts-ignore
import "./TeacherAdmins.scss";

const VISIBLE_SUBJECT_CHIPS = 2;

const labelOf = (
  item:
    | {
        value?: { en?: string; ar?: string };
        name?: { en?: string; ar?: string };
      }
    | undefined,
  langKey: "en" | "ar",
) =>
  item?.value?.[langKey] ||
  item?.value?.en ||
  item?.name?.[langKey] ||
  item?.name?.en ||
  "";

const parseSubjectLabel = (label: string) => {
  const parts = label
    .split(/\s*\/\s*|\s*-\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return {
      name: parts[parts.length - 1],
      meta: parts.slice(0, -1).join(" / "),
    };
  }
  return { name: label, meta: "" };
};

type AssignedSubjectsCellProps = {
  subjects: SelectListDto[];
  langKey: "en" | "ar";
  rowId: string;
  onManage: () => void;
};

const AssignedSubjectsCell: React.FC<AssignedSubjectsCellProps> = ({
  subjects,
  langKey,
  rowId,
  onManage,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (!subjects?.length) {
    return (
      <button
        type="button"
        className="btn btn-sm btn-soft-primary"
        onClick={onManage}
      >
        <i className="ri-add-line me-1"></i>
        {t("assignSubjects")}
      </button>
    );
  }

  const visible = expanded
    ? subjects
    : subjects.slice(0, VISIBLE_SUBJECT_CHIPS);
  const hiddenCount = subjects.length - VISIBLE_SUBJECT_CHIPS;
  const allLabels = subjects
    .map((s) => (langKey === "ar" ? s.value.ar : s.value.en))
    .join("\n");

  return (
    <div className="admin-teachers-subjects-cell py-1">
      <div className="subjects-chip-list">
        {visible.map((subject) => {
          const full = langKey === "ar" ? subject.value.ar : subject.value.en;
          const { name, meta } = parseSubjectLabel(full || "");
          return (
            <button
              key={subject.id}
              type="button"
              className="subject-chip subject-chip-btn"
              title={full}
              onClick={onManage}
            >
              <span className="subject-chip-name">{name}</span>
              {meta ? <span className="subject-chip-meta">{meta}</span> : null}
            </button>
          );
        })}
        {!expanded && hiddenCount > 0 ? (
          <>
            <button
              type="button"
              id={`subjects-more-${rowId}`}
              className="subject-chip-more"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(true);
              }}
            >
              +{hiddenCount} {t("more")}
            </button>
            <UncontrolledTooltip
              target={`subjects-more-${rowId}`}
              placement="top"
            >
              {allLabels}
            </UncontrolledTooltip>
          </>
        ) : null}
        {expanded && subjects.length > VISIBLE_SUBJECT_CHIPS ? (
          <button
            type="button"
            className="subject-chip-more"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(false);
            }}
          >
            {t("showLess")}
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-sm btn-soft-primary"
          title={t("manageSubjects")}
          onClick={onManage}
        >
          <i className="ri-pencil-line"></i>
        </button>
      </div>
    </div>
  );
};

type TProps = {};
export const TeacherAdminsPage: React.FC<TProps> = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const initState = new PaginateQuery();
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [subjectsTeacher, setSubjectsTeacher] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [queryReady, setQueryReady] = useState(false);

  const [educationSystemId, setEducationSystemId] = useState("");
  const [curriculumId, setCurriculumId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [stageId, setStageId] = useState("");
  const [gradeId, setGradeId] = useState("");
  const [subjectId, setSubjectId] = useState("");

  const { data: educationSystems } = useGetEducationSystemSelectListQuery();
  const { data: curricula } = useGetCurriculumSelectListQuery(
    educationSystemId || undefined,
    { skip: !educationSystemId },
  );
  const { data: courses } = useGetCourseSelectListQuery();

  const stagesQuery = useMemo(() => {
    const q = new PaginateQuery();
    q.limit = 1000;
    q.page = 1;
    q.sortBy = "sequence_number:ASC";
    q.filter = { curriculumId, courseId };
    return q;
  }, [curriculumId, courseId]);

  const { data: stagesResponse } = useGetEducationalStagesQuery(stagesQuery, {
    skip: !curriculumId || !courseId,
  });
  const stages: EducationalStageDto[] = stagesResponse?.data || [];
  const selectedStage = useMemo(
    () => stages.find((s) => s.id === stageId),
    [stages, stageId],
  );
  const grades = selectedStage?.grades || [];

  const { data: gradeSubjects = [] } = useGetSubjectsSelectListByGradeQuery(
    gradeId,
    { skip: !gradeId },
  );

  const {
    data: response,
    isLoading: loading,
    refetch,
  } = useGetTeacherAdminUsersQuery(
    { role: RoleEnum.TEACHER_ADMIN, data: paginateQuery },
    { skip: !queryReady },
  );

  const [
    deleteTeacherAdminUser,
    {
      isLoading: deleteTeacherAdminUserLoading,
      error: deleteTeacherAdminUserError,
    },
  ] = useDeleteTeacherAdminUserMutation();

  const [updateTeacherAdminUserProfit] =
    useUpdateTeacherAdminUserProfitMutation();

  document.title = `${t("documentTitle", { pageName: "teacherAdmins" })}`;

  const columns: TableColumn<TeacherAdminDto>[] = [
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => <TextColumn text={row.name[langKey]} />,
      sortable: true,
      grow: 1.2,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
      width: "150px",
    },
    {
      name: <ColumnHeader title="share" />,
      sortable: false,
      width: "130px",
      cell: (row) => (
        <AmountInput
          id={row.id}
          initialValue={{
            profit: row.profit,
            profitLastUpdatedOn: row.profitLastUpdatedOn,
          }}
          onSave={async (id, amount) => {
            await updateTeacherAdminUserProfit({
              id,
              data: { profit: amount },
            }).unwrap();
          }}
        />
      ),
    },
    {
      name: <ColumnHeader title="subjects" />,
      cell: (row) => (
        <AssignedSubjectsCell
          subjects={row.subjects || []}
          langKey={langKey}
          rowId={row.id}
          onManage={() =>
            setSubjectsTeacher({
              id: row.id,
              name: row.name?.[langKey] || row.name?.en || "",
            })
          }
        />
      ),
      sortable: false,
      grow: 2,
      wrap: true,
    },
    {
      name: <ColumnHeader title="primaryPhoneNumber" />,
      cell: (row) => (
        <a
          style={{ cursor: "pointer" }}
          href={`tel:+${row.phoneObj?.countryCode}${row.phoneObj?.phoneNumber}`}
        >
          <u dir="ltr">{`+${row.phoneObj?.countryCode}${row.phoneObj?.phoneNumber}`}</u>
        </a>
      ),
      sortable: false,
      width: "150px",
    },
    {
      name: <ColumnHeader title="secondaryPhoneNumber" />,
      cell: (row) =>
        row.secondaryPhoneCountryCode && row.secondaryPhonePhoneNumber ? (
          <a
            style={{ cursor: "pointer" }}
            href={`tel:+${row.secondaryPhoneCountryCode}${row.secondaryPhonePhoneNumber}`}
          >
            <u dir="ltr">{`+${row.secondaryPhoneCountryCode}${row.secondaryPhonePhoneNumber}`}</u>
          </a>
        ) : (
          <span className="text-muted">—</span>
        ),
      sortable: false,
      width: "150px",
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <ActionButton
            onAction={() => onEdit(row.id)}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          <ActionButton
            onAction={() => onDelete(row.id)}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    switch (name) {
      case "name.en":
      case "name.ar":
        return 1;
      default:
        return 2;
    }
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("created_at:DESC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    const educationSystemIdParam =
      queryParams.get("filter[educationSystemId]") || "";
    const curriculumIdParam = queryParams.get("filter[curriculumId]") || "";
    const courseIdParam = queryParams.get("filter[courseId]") || "";
    const stageIdParam = queryParams.get("filter[stageId]") || "";
    const gradeIdParam = queryParams.get("filter[gradeId]") || "";
    const subjectParam = queryParams.get("filter[subject]") || "";
    const singleSubjectId =
      subjectParam && !subjectParam.includes(",") ? subjectParam : "";

    setEducationSystemId(educationSystemIdParam);
    setCurriculumId(curriculumIdParam);
    setCourseId(courseIdParam);
    setStageId(stageIdParam);
    setGradeId(gradeIdParam);
    setSubjectId(singleSubjectId);

    const query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
      filter: {
        educationSystemId: educationSystemIdParam,
        curriculumId: curriculumIdParam,
        courseId: courseIdParam,
        stageId: stageIdParam,
        gradeId: gradeIdParam,
        subject: singleSubjectId || subjectParam,
      },
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length === 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string),
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery(query);
    setQueryReady(true);
  }, []);

  useEffect(() => {
    if (!queryReady) return;
    const { limit, page, search, sortBy, filter } = paginateQuery;
    let query = `?page=${page || 1}`;
    if (limit) query += `&limit=${limit}`;
    if (sortBy) query += `&sortBy=${sortBy}`;
    if (search) query += `&search=${search}`;
    if (filter && Object.keys(filter).length > 0) {
      Object.keys(filter).forEach((key: string) => {
        if (filter[key]) query += `&filter[${key}]=${filter[key]}`;
      });
    }
    if (query !== history.location.search) {
      history.replace({ search: query });
    }
  }, [paginateQuery, queryReady]);

  // Apply hierarchy filters to the list query.
  useEffect(() => {
    if (!queryReady) return;

    const nextFilter = {
      educationSystemId,
      curriculumId,
      courseId,
      stageId,
      gradeId,
      subject: subjectId,
    };
    const current = paginateQuery.filter || {};
    const unchanged =
      (current.educationSystemId || "") === educationSystemId &&
      (current.curriculumId || "") === curriculumId &&
      (current.courseId || "") === courseId &&
      (current.stageId || "") === stageId &&
      (current.gradeId || "") === gradeId &&
      (current.subject || "") === subjectId;
    if (unchanged) return;

    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        ...nextFilter,
      },
    }));
  }, [
    educationSystemId,
    curriculumId,
    courseId,
    stageId,
    gradeId,
    subjectId,
    queryReady,
  ]);

  useEffect(() => {
    if (!curriculumId || !courseId) return;
    if (!stages.length) return;
    if (stageId && !stages.some((s) => s.id === stageId)) {
      setStageId("");
    }
  }, [stages, stageId, curriculumId, courseId]);

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteTeacherAdminUser(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {}
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText, page: 1 });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "name" + "." + i18n.language;
        break;
      default:
        sortBy = "created_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<TeacherAdminDto>,
    sortDirection: string,
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  const clearHierarchyFilters = () => {
    setEducationSystemId("");
    setCurriculumId("");
    setCourseId("");
    setStageId("");
    setGradeId("");
    setSubjectId("");
    setPaginateQuery({
      ...paginateQuery,
      page: 1,
      filter: {
        educationSystemId: "",
        curriculumId: "",
        courseId: "",
        stageId: "",
        gradeId: "",
        subject: "",
      },
    });
  };

  const hasHierarchyFilters =
    !!educationSystemId ||
    !!curriculumId ||
    !!courseId ||
    !!stageId ||
    !!gradeId ||
    !!subjectId;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("teacherAdmins")} pageTitle={t("cpUsers")} />
          <Alert color="info" className="mb-3">
            <ul className="mb-0">
              <li>{t("roles.teacherAdmin.line1")}</li>
              <li>{t("roles.teacherAdmin.line2")}</li>
            </ul>
          </Alert>
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-2 align-items-end">
                    <Col sm={4} md={3}>
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <div className="list-grid-nav hstack gap-1">
                        <Button color="success" onClick={() => onAdd()}>
                          <i className="ri-add-fill me-1 align-bottom"></i>
                          {t("addItem", { item: "teacherAdmin" })}
                        </Button>
                        <Button
                          color="success"
                          outline
                          onClick={() => refetch()}
                        >
                          <i className="ri-refresh-line me-1 align-bottom"></i>
                          {t("refresh")}
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <div className="admin-teachers-filters border rounded p-3 mb-3 bg-light bg-opacity-50">
                    <Row className="g-2 align-items-end">
                      <Col md={2}>
                        <Label className="form-label">
                          {t("educationSystem")}
                        </Label>
                        <Input
                          type="select"
                          value={educationSystemId}
                          onChange={(e) => {
                            setEducationSystemId(e.target.value);
                            setCurriculumId("");
                            setStageId("");
                            setGradeId("");
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {(educationSystems || []).map((s: any) => (
                            <option key={s.id} value={s.id}>
                              {labelOf(s, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">
                          {t("curriculumItem")}
                        </Label>
                        <Input
                          type="select"
                          value={curriculumId}
                          disabled={!educationSystemId}
                          onChange={(e) => {
                            setCurriculumId(e.target.value);
                            setStageId("");
                            setGradeId("");
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {(curricula || []).map((c: any) => (
                            <option key={c.id} value={c.id}>
                              {labelOf(c, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">{t("course")}</Label>
                        <Input
                          type="select"
                          value={courseId}
                          onChange={(e) => {
                            setCourseId(e.target.value);
                            setStageId("");
                            setGradeId("");
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {(courses || []).map((c: any) => (
                            <option key={c.id} value={c.id}>
                              {labelOf(c, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">
                          {t("educationalStage")}
                        </Label>
                        <Input
                          type="select"
                          value={stageId}
                          disabled={!curriculumId || !courseId}
                          onChange={(e) => {
                            setStageId(e.target.value);
                            setGradeId("");
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {stages.map((stage) => (
                            <option key={stage.id} value={stage.id}>
                              {stage.name[langKey] || stage.name.en}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">{t("grade")}</Label>
                        <Input
                          type="select"
                          value={gradeId}
                          disabled={!stageId}
                          onChange={(e) => {
                            setGradeId(e.target.value);
                            setSubjectId("");
                          }}
                        >
                          <option value="">{t("all")}</option>
                          {grades.map((grade) => (
                            <option key={grade.id} value={grade.id}>
                              {grade.name[langKey] || grade.name.en}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="form-label">{t("subject")}</Label>
                        <Input
                          type="select"
                          value={subjectId}
                          disabled={!gradeId}
                          onChange={(e) => setSubjectId(e.target.value)}
                        >
                          <option value="">{t("all")}</option>
                          {gradeSubjects.map((s: SelectListDto) => (
                            <option key={s.id} value={s.id}>
                              {labelOf(s, langKey)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                    </Row>
                    {hasHierarchyFilters ? (
                      <div className="mt-2 text-end">
                        <Button
                          color="link"
                          size="sm"
                          className="p-0"
                          onClick={clearHierarchyFilters}
                        >
                          <i className="ri-close-circle-line me-1"></i>
                          {t("clearFilters")}
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  <Row>
                    <Col sm={12}>
                      <AppTable
                        theme={"dark"}
                        columns={columns}
                        data={response?.data || []}
                        progressPending={loading}
                        progressComponent={<TableSpinner />}
                        pagination
                        paginationServer
                        sortServer
                        onSort={onSort}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                        onChangePage={onChangePage}
                        defaultSortFieldId={defaultSortFieldId}
                        paginationDefaultPage={defaultPage}
                        defaultSortAsc={defaultSortAsc}
                        persistTableHead
                        paginationTotalRows={response?.meta?.totalItems}
                        paginationComponentOptions={{
                          rowsPerPageText: t("rowsPerPage"),
                          rangeSeparatorText: t("of"),
                        }}
                        fixedHeader={true}
                        fixedHeaderScrollHeight="60vh"
                      />
                    </Col>
                  </Row>
                  <DeleteModal
                    loading={deleteTeacherAdminUserLoading}
                    show={showDeleteModal}
                    onDeleteClick={() => onDeleteConfirm()}
                    onCloseClick={() => {
                      setShowDeleteModal(false);
                    }}
                    error={
                      deleteTeacherAdminUserError
                        ? (deleteTeacherAdminUserError as any)?.data?.message ||
                          "An error occurred"
                        : undefined
                    }
                  />
                  <AddEditAdminTeacherModal
                    id={editingItemId}
                    show={showAddEditModal}
                    onCloseClick={() => {
                      setShowAddEditModal(false);
                      setEditingItemId(null);
                    }}
                  />
                  <TeacherSubjectsModal
                    show={!!subjectsTeacher}
                    teacherId={subjectsTeacher?.id}
                    teacherName={subjectsTeacher?.name}
                    onCloseClick={() => setSubjectsTeacher(null)}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

type AmountInputProps = {
  id: string;
  initialValue?: {
    profit: number;
    profitLastUpdatedOn: Date;
  };
  onSave: (id: string, amount: number) => Promise<void> | void;
};

const AmountInput: React.FC<AmountInputProps> = ({
  id,
  initialValue,
  onSave,
}) => {
  const initialProfit =
    initialValue?.profit !== undefined ? String(initialValue.profit) : "";

  const [value, setValue] = useState(initialProfit);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(initialProfit);
    setIsDirty(false);
  }, [initialProfit]);

  const handleChange = (val: string) => {
    setValue(val);
    setIsDirty(val !== initialProfit);
  };

  const handleSave = async () => {
    if (value === "" || Number.isNaN(Number(value))) return;

    try {
      setSaving(true);
      await onSave(id, Number(value));
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-1 py-2">
      <div className="d-flex align-items-center gap-1">
        <input
          type="number"
          step="0.01"
          min="0"
          className="form-control form-control-sm"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
        />

        {isDirty && (
          <Button
            size="sm"
            color="success"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="ri-save-line" />
            )}
          </Button>
        )}
      </div>

      {initialValue?.profitLastUpdatedOn && (
        <small className="text-muted">
          {new Date(initialValue.profitLastUpdatedOn).toLocaleDateString()}{" "}
          {new Date(initialValue.profitLastUpdatedOn).toLocaleTimeString()}
        </small>
      )}
    </div>
  );
};
